/* ============================================================================
   MALSERA — ms-header.js
   Grupo sticky (barra de anuncios + cabecera), medida de --header-h y menú
   lateral mínimo. Componentes web nativos, sin framework.
   ============================================================================ */

/**
 * Mide en runtime el alto real del grupo sticky y lo escribe en --header-h
 * sobre :root. Toda posición sticky y todo cálculo de altura de la web lee
 * esa variable. Nunca se codifica un alto fijo: la barra de anuncios cambia
 * de alto según el mensaje y el ancho de pantalla.
 */
class MsHeaderGroup extends HTMLElement {
  connectedCallback() {
    this.medir();

    if ('ResizeObserver' in window) {
      this.observador = new ResizeObserver(() => this.medir());
      this.observador.observe(this);
    } else {
      this.alRedimensionar = () => this.medir();
      window.addEventListener('resize', this.alRedimensionar);
    }
  }

  disconnectedCallback() {
    if (this.observador) this.observador.disconnect();
    if (this.alRedimensionar) window.removeEventListener('resize', this.alRedimensionar);
  }

  medir() {
    const alto = Math.round(this.getBoundingClientRect().height);
    if (alto > 0 && alto !== this.ultimoAlto) {
      this.ultimoAlto = alto;
      document.documentElement.style.setProperty('--header-h', `${alto}px`);
    }
  }
}
customElements.define('ms-header-group', MsHeaderGroup);

/**
 * Franja de búsqueda. La lupa es un enlace real a /search: aquí solo se
 * intercepta el clic para abrir la franja en su lugar. Sin este archivo, la
 * lupa sigue llevando a la página de resultados.
 *
 * El alto de la franja NO se toca desde aquí. Lo anima el CSS con
 * grid-template-rows y lo mide el ResizeObserver de MsHeaderGroup, que
 * reescribe --header-h solo. Es la razón de que la franja viva dentro del
 * grupo sticky.
 */
class MsSearch {
  constructor() {
    this.franja = document.querySelector('[data-ms-search]');
    this.disparador = document.querySelector('[data-ms-search-open]');
    if (!this.franja || !this.disparador) return;

    this.campo = this.franja.querySelector('[data-ms-search-input]');
    this.cerrar = this.franja.querySelector('[data-ms-search-close]');

    this.disparador.addEventListener('click', (evento) => {
      evento.preventDefault();
      this.franja.classList.contains('is-open') ? this.cerrarFranja() : this.abrir();
    });

    this.cerrar?.addEventListener('click', () => this.cerrarFranja());

    // Escape cierra desde cualquier sitio de la franja, no solo desde el campo.
    this.franja.addEventListener('keydown', (evento) => {
      if (evento.key === 'Escape') this.cerrarFranja();
    });
  }

  abrir() {
    this.franja.classList.add('is-open');
    this.franja.removeAttribute('inert');
    this.disparador.setAttribute('aria-expanded', 'true');

    // El foco va al campo cuando la franja ya tiene alto. Enfocar un elemento
    // de altura cero hace que el navegador desplace la página a un sitio raro.
    requestAnimationFrame(() => this.campo?.focus());
  }

  cerrarFranja() {
    if (!this.franja.classList.contains('is-open')) return;
    this.franja.classList.remove('is-open');
    this.franja.setAttribute('inert', '');
    this.disparador.setAttribute('aria-expanded', 'false');

    // El foco vuelve a la lupa. Si no, se queda en un elemento que acaba de
    // volverse inerte y el tabulador reaparece al principio de la página.
    this.disparador.focus();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new MsSearch(), { once: true });
} else {
  new MsSearch();
}

/**
 * Barra de anuncios. Splide en bucle, 5s, deslizamiento de 600ms.
 * Se detiene al pasar el cursor y al recibir foco.
 * Con un solo mensaje no se inicializa nada: no hay carrusel de uno.
 */
class MsAnnouncement extends HTMLElement {
  connectedCallback() {
    const carrusel = this.querySelector('.splide');
    if (!carrusel) return;

    const mensajes = carrusel.querySelectorAll('.splide__slide');
    if (mensajes.length < 2) {
      carrusel.classList.add('is-initialized');
      return;
    }

    const arrancar = () => {
      if (typeof Splide === 'undefined') return;

      this.splide = new Splide(carrusel, {
        type: 'loop',
        arrows: false,
        pagination: false,
        autoplay: true,
        interval: 5000,
        speed: 600,
        pauseOnHover: true,
        pauseOnFocus: true,
        drag: false,
        i18n: { carousel: 'Anuncios' },
      }).mount();

      this.querySelector('.ms-arrow--prev')?.addEventListener('click', () => this.splide.go('<'));
      this.querySelector('.ms-arrow--next')?.addEventListener('click', () => this.splide.go('>'));
    };

    if (typeof Splide === 'undefined') {
      document.addEventListener('ms:splide-listo', arrancar, { once: true });
    } else {
      arrancar();
    }
  }

  disconnectedCallback() {
    this.splide?.destroy();
  }
}
customElements.define('ms-announcement', MsAnnouncement);

/**
 * Menú lateral mínimo. Está fuera del alcance del PRD de ficha: existe solo
 * para que la ficha sea navegable. Se reemplaza con el PRD de la home.
 */
class MsNav extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('.ms-nav');
    this.overlay = this.querySelector('[data-ms-nav-overlay]');
    this.disparador = document.querySelector('[data-ms-nav-open]');

    this.disparador?.addEventListener('click', () => this.abrir());
    this.overlay?.addEventListener('click', () => this.cerrar());
    this.querySelector('[data-ms-nav-close]')?.addEventListener('click', () => this.cerrar());

    this.alPulsarTecla = (evento) => {
      if (evento.key === 'Escape') this.cerrar();
    };
  }

  abrir() {
    this.panel?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.addEventListener('keydown', this.alPulsarTecla);
    this.panel?.querySelector('a, button')?.focus();
  }

  cerrar() {
    this.panel?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.removeEventListener('keydown', this.alPulsarTecla);
    this.disparador?.focus();
  }
}
customElements.define('ms-nav', MsNav);
